import type { UserOnboarding } from "@cohesive/api-client";
import { useMemo } from "react";
import { useOnboardingRecord } from "../status";
import type { TourId } from "../types";

/**
 * Where a tour is: the canvas it runs on, the step the user is on, and
 * whatever the steps have kept (the dropped brief's node id and position, the
 * image and deck node ids, the tour session id).
 *
 * It lives on the onboarding record, in the two maps the API merges per key.
 * `refs` names the canvas and holds the steps' ids; `steps` stamps each step
 * as it is reached, so the highest one stamped is where the user is. That is
 * what lets another browser — or a second tab — resume mid-tour.
 */
export interface TourProgress {
  canvasId: string;
  step: number;
  /** When the step was entered; null for step 1, which has no stamp. */
  enteredAt: string | null;
  data: Record<string, string>;
  /** The person finished the last step. Completion is counted from this. */
  done: boolean;
  /** The person left through "Exit tour" and has not picked it up since. */
  exited: boolean;
}

export const refKey = (tour: TourId): string => `tour:${tour}`;
export const stepKey = (tour: TourId, step: number): string =>
  `tour:${tour}:${step}`;
export const doneKey = (tour: TourId): string => `tour:${tour}:done`;
export const exitKey = (tour: TourId): string => `tour:${tour}:exit`;
export const dataKey = (tour: TourId, key: string): string =>
  `tour:${tour}:data:${key}`;

const dataPrefix = (tour: TourId): string => `tour:${tour}:data:`;

/** Null when the record does not name a canvas for this tour. */
export function readProgress(
  record: UserOnboarding | undefined,
  tour: TourId,
): TourProgress | null {
  const canvasId = record?.refs?.[refKey(tour)];
  if (!canvasId) return null;

  const steps = record?.steps ?? {};
  const prefix = `tour:${tour}:`;
  let step = 1;
  for (const key of Object.keys(steps)) {
    if (!key.startsWith(prefix)) continue;
    const reached = Number(key.slice(prefix.length));
    if (Number.isInteger(reached) && reached > step) step = reached;
  }

  const data: Record<string, string> = {};
  for (const [key, value] of Object.entries(record?.refs ?? {})) {
    if (key.startsWith(dataPrefix(tour))) {
      data[key.slice(dataPrefix(tour).length)] = value;
    }
  }

  return {
    canvasId,
    step,
    enteredAt: steps[stepKey(tour, step)] ?? null,
    data,
    done: doneKey(tour) in steps,
    exited: exitKey(tour) in steps,
  };
}

export function useTourProgress(tour: TourId): TourProgress | null {
  const { record } = useOnboardingRecord();
  return useMemo(() => readProgress(record, tour), [record, tour]);
}

const now = (): string => new Date().toISOString();

/**
 * Point the tour at a canvas and drop everything it held for the last one: a
 * second run must not inherit node ids from a canvas that is gone.
 */
export function openTourPatch(
  record: UserOnboarding | undefined,
  tour: TourId,
  canvasId: string,
): UserOnboarding {
  const prefix = `tour:${tour}:`;
  const steps: Record<string, string> = {};
  for (const key of Object.keys(record?.steps ?? {})) {
    if (key.startsWith(prefix)) steps[key] = "";
  }
  const refs: Record<string, string> = { [refKey(tour)]: canvasId };
  for (const key of Object.keys(record?.refs ?? {})) {
    if (key.startsWith(dataPrefix(tour))) refs[key] = "";
  }
  return { steps, refs };
}

/** Stamps the step as it is entered, with whatever the last one kept. */
export function stepPatch(
  tour: TourId,
  step: number,
  data: Record<string, string> = {},
): UserOnboarding {
  return {
    steps: { [stepKey(tour, step)]: now() },
    ...(Object.keys(data).length > 0 ? dataPatch(tour, data) : {}),
  };
}

/** The last step was finished: the only writer of the done stamp. */
export function finishPatch(tour: TourId): UserOnboarding {
  return { global: "complete", steps: { [doneKey(tour)]: now() } };
}

/**
 * The person left through "Exit tour". Never the done stamp: an exit that
 * wrote it would count as a finish.
 */
export function exitPatch(tour: TourId): UserOnboarding {
  return { global: "skipped", steps: { [exitKey(tour)]: now() } };
}

/** Picked up again from the home: the stamped step runs on the same canvas. */
export function resumePatch(tour: TourId): UserOnboarding {
  return { global: "in_progress", steps: { [exitKey(tour)]: "" } };
}

export function dataPatch(
  tour: TourId,
  patch: Record<string, string>,
): UserOnboarding {
  const refs: Record<string, string> = {};
  for (const [key, value] of Object.entries(patch)) {
    refs[dataKey(tour, key)] = value;
  }
  return { refs };
}
